// DS: C85A, C85A_M
import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiDataHook, BiEventType } from '@wix/da-bi/pkg/constants';
import StandardMenu from '@wix/da-shared-react/pkg/Dropdown/MenuPreset/StandardMenu';
import { TabBarProps } from './types';
import SearchBar from './_partials/SearchBar';
import MenuItem from './_partials/MenuItem';
import MoreMenuItem from './_partials/MenuItem/MoreMenuItem';
import { useScrollToActiveItem } from './hooks/useScrollToActiveItem';
import { useIsOverflowedHorizontal } from './hooks/useIsOverflowedHorizontal';

import s from './Header.scss';

const DESKTOP_MAX_VISIBLE_ITEMS = 5;

const Header: React.FC<TabBarProps> = ({
  isSearchPage,
  pageType,
  items = [],
  title,
  tagline,
  rightSideContent,
  activeMenuItem,
  children,
  className,
  titleClassName,
  withNavigation = true,
  isSticky: isStickyProp,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { navListRef, activeItemRef } = useScrollToActiveItem(isMobile, 0);
  const { isOverflowed } = useIsOverflowedHorizontal(navListRef);

  const actualItems = isMobile
    ? items
    : items.slice(0, DESKTOP_MAX_VISIBLE_ITEMS);
  const overflowItems = isMobile
    ? []
    : items.slice(DESKTOP_MAX_VISIBLE_ITEMS).map(menuItem => ({
        link: menuItem.url,
        label: t(`browseHeader.page.${menuItem.subnav}`),
        biData: {
          evid: BiEventType.CONTENT_NAVBAR_CLICK,
          link_name: menuItem.subnav,
        },
      }));

  const setActiveItemRef = (page?: string) =>
    page === activeMenuItem
      ? (node: HTMLLIElement) => {
          activeItemRef.current = node;
        }
      : undefined;

  const titleBlock = title && (
    <div
      className={classnames(
        s['title'],
        !withNavigation && s['no-background'],
        titleClassName
      )}
    >
      <h1 className={s['headline']}>{title}</h1>
      {tagline && <div className={s['tagline']}>{tagline}</div>}
    </div>
  );

  const isSticky = isStickyProp ?? (!isMobile || withNavigation);

  return (
    <>
      {isSearchPage && <SearchBar isMobile={isMobile} pageType={pageType} />}
      {isMobile && titleBlock}
      {(!isMobile || withNavigation || rightSideContent) && (
        <div
          className={classnames(
            s['header-container'],
            isSticky && s['sticky'],
            className
          )}
        >
          <header className={s['header']} data-hook={BiDataHook.DEVIATION_NAV}>
            {!isMobile && titleBlock}
            {withNavigation && (
              <div
                className={classnames(
                  s['navigation'],
                  isOverflowed && s['scrollable']
                )}
                ref={navListRef}
              >
                {children}
                {!children && actualItems.length > 0 && (
                  <ul className={s['nav-list']}>
                    {actualItems.map(menuItem => (
                      <li
                        key={menuItem.subnav}
                        className={classnames(
                          s['nav-list__item'],
                          activeMenuItem === menuItem.subnav && s['active']
                        )}
                        ref={setActiveItemRef(menuItem.subnav)}
                      >
                        <MenuItem
                          menuItem={menuItem}
                          isActive={activeMenuItem === menuItem.subnav}
                          className={s['nav-list__item__link']}
                        />
                      </li>
                    ))}
                    {overflowItems.length > 0 && (
                      <li
                        className={classnames(
                          s['nav-list__item'],
                          s['more-menu']
                        )}
                      >
                        <MoreMenuItem className={s['nav-list__item__link']} />
                        <StandardMenu
                          lightText
                          id="subnav-more"
                          className={s['more-list']}
                          itemClassName={s['more-list__item']}
                          items={overflowItems}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
            <div className={s['right-side']}>{rightSideContent}</div>
          </header>
        </div>
      )}
    </>
  );
};
Header.displayName = 'Header';

export default Header;
